import { useEffect, useMemo, useState } from "react";
import { EventType } from "@azure/msal-browser";

import { initializeMsal } from "../initializeMsal";
import { loginRequest } from "../msalConfig";
import axiosManager from "services/axiosManager";

const useAuthBootstrap = () => {
    const [msalInstance, setMsalInstance] = useState(null);
    const [isReady, setIsReady] = useState(false);
    const [initError, setInitError] = useState(null);
    const [accounts, setAccounts] = useState([]);
    const [activeAccount, setActiveAccount] = useState(null);

    useEffect(() => {
        let isMounted = true;
        let callbackId;
        let activeInstance;

        const setAxiosTokenFromAccount = async (instance, account) => {
            if (!instance || !account) {
                return;
            }

            try {
                const response = await instance.acquireTokenSilent({
                    ...loginRequest,
                    account,
                });

                axiosManager.setAuthToken(`Bearer ${response.idToken}`);
            } catch (error) {
                console.error("Failed to acquire access token", error);
            }
        };

        initializeMsal()
            .then(async (instance) => {
                if (!isMounted) {
                    return;
                }

                activeInstance = instance;
                const currentAccount = instance.getActiveAccount();

                if (!currentAccount) {
                    // No account after redirect processing — trigger login.
                    // The page will navigate away, so keep isReady false (loading).
                    setMsalInstance(instance);
                    await instance.loginRedirect(loginRequest);
                    return;
                }

                await setAxiosTokenFromAccount(instance, currentAccount);

                setMsalInstance(instance);
                setAccounts(instance.getAllAccounts());
                setActiveAccount(currentAccount);
                setIsReady(true);

                callbackId = instance.addEventCallback((event) => {
                    if (event.eventType === EventType.LOGOUT_SUCCESS) {
                        axiosManager.setAuthToken(null);
                        setActiveAccount(null);
                        setAccounts(instance.getAllAccounts());
                    }
                });
            })
            .catch((error) => {
                if (!isMounted) {
                    return;
                }

                setInitError(error);
                setIsReady(true);
            });

        return () => {
            isMounted = false;
            if (activeInstance && callbackId) {
                activeInstance.removeEventCallback(callbackId);
            }
        };
    }, []);

    return useMemo(() => {
        const roles = activeAccount?.idTokenClaims?.roles ?? [];
        const isAdmin = roles.includes("USPA.Admin");

        return {
            isReady,
            error: initError,
            instance: msalInstance,
            accounts,
            activeAccount,
            roles,
            isAdmin,
        };
    }, [isReady, initError, msalInstance, accounts, activeAccount]);
};

export { useAuthBootstrap };
